import React, { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { BriefRecorder } from "@/components/BriefRecorder";

export function CalEmbed({ calLink }) {
  useEffect(() => {
    const w = window;
    (function (C, A, L) {
      const p = function (a, ar) { a.q.push(ar); };
      const d = C.document;
      C.Cal = C.Cal || function () {
        const cal = C.Cal;
        const ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () { p(api, arguments); };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(w, "https://app.cal.com/embed/embed.js", "init");
    w.Cal("init", { origin: "https://cal.com" });
    w.Cal("inline", {
      elementOrSelector: "#cal-inline-embed",
      calLink,
      config: { theme: "light" },
    });
  }, [calLink]);

  return (
    <div
      id="cal-inline-embed"
      data-testid="cal-inline-embed"
      data-cal-link={calLink}
      style={{ width: "100%", minHeight: 420, border: "1px solid var(--li)", overflow: "auto" }}
    />
  );
}

const EMPTY = { nom: "", societe: "", email: "", telephone: "", besoin: "", budget: "", echeance: "", message: "", website: "" };

export function ContactForm({ contactSec, type = "contact", compact = false, titreBouton = "Envoyer la demande" }) {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, type }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Envoi impossible pour le moment.");
      }
      setState("sent");
      setForm(EMPTY);
    } catch (err) {
      setError(err.message);
      setState("idle");
    }
  };

  if (state === "sent") {
    return (
      <div style={{ borderTop: "1px solid var(--li)", paddingTop: 32 }} data-testid="contact-confirmation">
        <p className="t-h3" style={{ margin: "0 0 12px", fontSize: 22 }}>{contactSec?.confirmation || "Demande reçue. Réponse sous 24 h ouvrées."}</p>
        <p className="t-body" style={{ margin: 0 }}>Merci pour votre message. L'équipe SLIZ revient vers vous rapidement.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" style={{ display: "grid", gap: 26 }}>
      <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off"
        style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }} aria-hidden="true" />
      <div className={compact ? "grid gap-6" : "grid gap-6 sm:grid-cols-2"}>
        <div className="field">
          <label htmlFor="f-nom">Nom</label>
          <input id="f-nom" data-testid="field-nom" required value={form.nom} onChange={set("nom")} autoComplete="name" />
        </div>
        <div className="field">
          <label htmlFor="f-societe">Société</label>
          <input id="f-societe" data-testid="field-societe" value={form.societe} onChange={set("societe")} autoComplete="organization" />
        </div>
      </div>
      <div className={compact ? "grid gap-6" : "grid gap-6 sm:grid-cols-2"}>
        <div className="field">
          <label htmlFor="f-email">Email pro</label>
          <input id="f-email" data-testid="field-email" type="email" required value={form.email} onChange={set("email")} autoComplete="email" />
        </div>
        <div className="field">
          <label htmlFor="f-tel">Téléphone</label>
          <input id="f-tel" data-testid="field-telephone" type="tel" value={form.telephone} onChange={set("telephone")} autoComplete="tel" />
        </div>
      </div>
      {!compact && (
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="field">
            <label htmlFor="f-besoin">Besoin</label>
            <select id="f-besoin" data-testid="field-besoin" value={form.besoin} onChange={set("besoin")}>
              <option value="">Sélectionner</option>
              {(contactSec?.besoins || []).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="f-budget">Budget envisagé</label>
            <select id="f-budget" data-testid="field-budget" value={form.budget} onChange={set("budget")}>
              <option value="">Sélectionner</option>
              {(contactSec?.budgets || []).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="f-echeance">Échéance souhaitée</label>
            <select id="f-echeance" data-testid="field-echeance" value={form.echeance} onChange={set("echeance")}>
              <option value="">Sélectionner</option>
              {(contactSec?.echeances || ["Dès que possible", "Sous 1 mois", "Sous 3 mois", "Sous 6 mois", "Pas encore fixée"]).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>
      )}
      <div className="field">
        <label htmlFor="f-message">Projet en deux lignes</label>
        <textarea id="f-message" data-testid="field-message" value={form.message} onChange={set("message")} />
      </div>
      {error && <p className="t-body" style={{ color: "#8C1D18", margin: 0 }} data-testid="contact-error">{error}</p>}
      <div>
        <button type="submit" className="btn btn-primary" disabled={state === "sending"} data-testid="contact-submit">
          {state === "sending" ? "Envoi…" : titreBouton}
        </button>
      </div>
      <p className="t-body" style={{ fontSize: 12, color: "var(--ink3)", margin: 0 }} data-testid="contact-rgpd">
        En envoyant ce formulaire, vous acceptez que vos informations soient traitées conformément à notre <a href="/politique-de-confidentialite/" style={{ color: "inherit", textDecoration: "underline" }}>politique de confidentialité</a>.
      </p>
    </form>
  );
}

export function ContactBlock({ contactSec }) {
  if (!contactSec) return null;
  return (
    <section id="contact" className="sec wrap" data-testid="contact-section">
      <div className="sec-num">{contactSec.libelle}</div>
      <div>
        <Reveal><h2 className="t-h2" style={{ margin: 0 }}>{contactSec.titre}</h2></Reveal>
        <Reveal delay={0.1}><p className="t-chapo" style={{ marginTop: 22, maxWidth: 640 }}>
          {(contactSec.chapeau || "").split("\n").map((line, i) => (
            <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
          ))}
        </p></Reveal>
        <div className="grid gap-16 lg:grid-cols-2" style={{ marginTop: 56 }}>
          <Reveal>
            <div>
              <p className="t-label" style={{ marginBottom: 22 }}>Dictez votre brief</p>
              <BriefRecorder />
              <div style={{ borderTop: "1px solid var(--li)", marginTop: 48, paddingTop: 40 }}>
                <p className="t-label" style={{ marginBottom: 28 }}>Ou écrivez-nous</p>
                <ContactForm contactSec={contactSec} />
              </div>
              {(contactSec.contactNom || contactSec.contactTel || contactSec.contactEmail) && (
                <div style={{ borderTop: "1px solid var(--li)", marginTop: 40, paddingTop: 32 }} data-testid="contact-direct">
                  <p className="t-label" style={{ marginBottom: 18 }}>Poser une question</p>
                  {contactSec.contactNom && (
                    <p className="t-body" style={{ margin: "0 0 6px", fontWeight: 600, color: "var(--roi)" }}>
                      {contactSec.contactNom}{contactSec.contactRole ? ` - ${contactSec.contactRole}` : ""}
                    </p>
                  )}
                  {contactSec.contactTel && (
                    <p style={{ margin: "0 0 6px" }}>
                      <a href={`tel:${contactSec.contactTel.replace(/\s/g, "")}`} className="link-arrow" data-testid="contact-tel">{contactSec.contactTel}</a>
                    </p>
                  )}
                  {contactSec.contactEmail && (
                    <p style={{ margin: 0 }}>
                      <a href={`mailto:${contactSec.contactEmail}`} className="link-arrow" data-testid="contact-email">{contactSec.contactEmail}</a>
                    </p>
                  )}
                </div>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <p className="t-label" style={{ marginBottom: 28 }}>Ou réservez directement 30 minutes</p>
              <CalEmbed calLink={contactSec.calLink} />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
